import * as amplitude from '@amplitude/analytics-browser';

// ==================== Authentication Events ====================
export const AUTH_EVENTS = {
  LOGIN_SUCCESS: 'login_success',
  LOGOUT: 'logout',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',
};

// ==================== User Management Events ====================
export const USER_EVENTS = {
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
};

// ==================== Project Events ====================
export const PROJECT_EVENTS = {
  // Project Creation Flow
  CREATION_STARTED: 'project_creation_started',
  ADDRESS_SELECTED: 'project_address_selected',
  PRODUCT_SELECTED: 'project_product_selected',
  PAYMENT_MODE_SELECTED: 'project_payment_mode_selected',
  PROJECT_CREATED: 'project_created',
  
  // Project Workflow
  WORKFLOW_STEP_COMPLETED: 'project_workflow_step_completed',
  
  // Project Details
  INSURANCE_UPDATED: 'project_insurance_updated',
  INSURANCE_SCOPE_APPROVED: 'project_insurance_scope_approved',
  INSURANCE_MATERIAL_SAVED: 'project_insurance_material_saved',
  INSURANCE_LABOUR_SAVED: 'project_insurance_labour_saved',
  NOTE_CREATED: 'project_note_created',
  DOCUMENT_UPLOADED: 'project_document_uploaded',
  ESTIMATE_GENERATED: 'project_estimate_generated',
  INVOICE_CREATED: 'project_invoice_created',
  INVOICE_UPDATED: 'project_invoice_updated',
  INVOICE_EMAIL_SENT: 'project_invoice_email_sent',
  INVOICE_VIEWED: 'project_invoice_viewed',
  MATERIAL_ORDER_SENT: 'project_material_order_sent',
  MATERIAL_ORDER_CONFIRMED: 'project_material_order_confirmed',
  CONTRACTOR_ORDER_SENT: 'project_contractor_order_sent',
  CONTRACTOR_ORDER_CONFIRMED: 'project_contractor_order_confirmed',
  PROJECT_ARCHIVED: 'project_archived',
};

// ==================== Customer Events ====================
export const CUSTOMER_EVENTS = {
  CUSTOMER_VIEWED: 'customer_viewed',
  CUSTOMERS_PAGE_VIEWED: 'customers_page_viewed',
};

// ==================== Insurance Company Events ====================
export const INSURANCE_EVENTS = {
  COMPANY_CREATED: 'insurance_company_created',
  COMPANY_UPDATED: 'insurance_company_updated',
  COMPANY_DELETED: 'insurance_company_deleted',
  COMPANIES_PAGE_VIEWED: 'insurance_companies_page_viewed',
};

// ==================== Leads Events ====================
export const LEAD_EVENTS = {
  LEAD_VIEWED: 'lead_viewed',
  LEADS_PAGE_VIEWED: 'leads_page_viewed',
};

// ==================== Item Creation Events ====================
export const ITEM_CREATION_EVENTS = {
  // Product Systems
  PRODUCT_SYSTEM_CREATED: 'product_system_created',
  PRODUCT_SYSTEM_UPDATED: 'product_system_updated',
  PRODUCT_SYSTEM_ARCHIVED: 'product_system_archived',
  
  // Material Categories
  MATERIAL_CATEGORY_CREATED: 'material_category_created',
  MATERIAL_CATEGORY_UPDATED: 'material_category_updated',
  MATERIAL_CATEGORY_ARCHIVED: 'material_category_archived',
  
  // Material Measurements
  MATERIAL_MEASUREMENT_CREATED: 'material_measurement_created',
  MATERIAL_MEASUREMENT_UPDATED: 'material_measurement_updated',
  MATERIAL_MEASUREMENT_ARCHIVED: 'material_measurement_archived',
  MATERIAL_MEASUREMENT_ORDER_UPDATED: 'material_measurement_order_updated',
  
  // Material Items
  MATERIAL_ITEM_CREATED: 'material_item_created',
  MATERIAL_ITEM_UPDATED: 'material_item_updated',
  MATERIAL_ITEM_ARCHIVED: 'material_item_archived',
  
  // Labor Measurements
  LABOR_MEASUREMENT_CREATED: 'labor_measurement_created',
  LABOR_MEASUREMENT_UPDATED: 'labor_measurement_updated',
  LABOR_MEASUREMENT_ARCHIVED: 'labor_measurement_archived',
  LABOR_MEASUREMENT_ORDER_UPDATED: 'labor_measurement_order_updated',
  
  // Contractors
  CONTRACTOR_CREATED: 'contractor_created',
  CONTRACTOR_UPDATED: 'contractor_updated',
  CONTRACTOR_ARCHIVED: 'contractor_archived',
  
  // Labor Items
  LABOR_ITEM_CREATED: 'labor_item_created',
  LABOR_ITEM_UPDATED: 'labor_item_updated',
  LABOR_ITEM_ARCHIVED: 'labor_item_archived',
  
  // Tags
  TAG_CREATED: 'tag_created',
  TAG_UPDATED: 'tag_updated',
  TAG_DELETED: 'tag_deleted',
  
  // Suppliers
  SUPPLIER_CREATED: 'supplier_created',
  SUPPLIER_UPDATED: 'supplier_updated',
  SUPPLIER_ARCHIVED: 'supplier_archived',
  
  // Tab Navigation
  TAB_VIEWED: 'item_creation_tab_viewed',
};

// ==================== Page View Events ====================
export const PAGE_EVENTS = {
  PAGE_VIEWED: 'page_viewed',
};

// ==================== Initialization ====================
export const initAmplitude = () => {
  amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY || '', {
    defaultTracking: true,
  });
};

// ==================== Core Functions ====================
export const logEvent = (eventName: string, eventProperties?: Record<string, any>) => {
  amplitude.track(eventName, eventProperties);
};

export const setUserProperties = (userId: string, userProperties?: Record<string, any>) => {
  amplitude.setUserId(userId);
  if (userProperties) {
    const identifyEvent = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      identifyEvent.set(key, value);
    });
    amplitude.identify(identifyEvent);
  }
};

export const resetUser = () => {
  amplitude.reset();
};
